import validSvc from '../../validSvc';
import { TblViewSpec, LegacyTableView, TableViewFormData } from '../table-view.types';
import { TblCol, TblSort, TblSortDir, TblView } from '../table.types';
import { tableSortVM } from '../table_view_models';

/**
 * Creates a bulletproof default view that always includes ALL columns.
 * This should never be modified or saved - it's always regenerated fresh.
 * NOTE: This function should only be used internally to ensure ALL columns are available.
 * It does NOT preserve the original section structure - that should be handled separately.
 * @param colMap Map of column keys to TblCol objects
 * @param defaultViewKey Optional key for the default view (default: 'default')
 * @returns TblViewSpec representing the default view
 */
export function createDefaultViewFromColumns(
    colMap: Map<string, TblCol>,
    defaultViewKey: string = 'default',
): TblViewSpec {
    const allColumns = Array.from(colMap.keys());

    return {
        key: defaultViewKey,
        title: 'Default',
        sections: [
            {
                title: 'All Columns',
                cols: allColumns,
            },
        ],
        sortby: 'id', // Default sort
        sortdir: 'ASC',
    };
}

/**
 * Converts a list of raw table views to legacy format (LegacyTableView[]).
 * Only objects with the required legacy properties are included.
 * @param rawTableViews Array of raw table view objects
 * @returns Array of LegacyTableView
 */
export function convertToLegacyTableViews(rawTableViews: any[]): LegacyTableView[] {
    return rawTableViews
        .map(view => {
            let pgsize = 50;
            if (typeof view.pgsize === 'number' && view.pgsize > 0) {
                pgsize = view.pgsize;
            }
            if (isLegacyTableViewFormat(view)) {
                return { ...view, pgsize } as LegacyTableView;
            }
            if (isUITableViewFormat(view)) {
                // Concatenate all columns from all sections
                const allCols = Array.isArray(view.sections)
                    ? view.sections.flatMap((section: any) => (Array.isArray(section.cols) ? section.cols : []))
                    : [];
                const cols = '|' + allCols.join('|') + '|';
                // IMPORTANT: Always encode sort direction in sortby string for legacy format
                // Example: sortby: "created" (ascending), sortby: "-created" (descending)
                let sortby = view.sortby;
                if (view.sortdir && typeof view.sortdir === 'string') {
                    if (view.sortdir.toLowerCase() === 'desc' && sortby && !sortby.startsWith('-')) {
                        sortby = '-' + sortby;
                    } else if (view.sortdir.toLowerCase() === 'asc' && sortby && sortby.startsWith('-')) {
                        sortby = sortby.substring(1);
                    }
                }
                return {
                    id: view.key,
                    title: view.title,
                    sortby,
                    pgsize,
                    cols,
                };
            }
            return null;
        })
        .filter((v): v is LegacyTableView => !!v);
}

export function unpackSortBy(sortBy: string): TblSort {
    if (!validSvc.isText(sortBy) || sortBy.length < 2) {
        return tableSortVM();
    }

    const dir: TblSortDir = sortBy.substring(0, 1) == '-' ? 'DSC' : 'ASC';
    return {
        col: dir == 'DSC' ? sortBy.substring(1) : sortBy,
        dir,
    };
}

export function isLegacyTableViewFormat(view: any): boolean {
    return view && typeof view === 'object' && ['id', 'cols', 'sortby', 'pgsize'].every(k => k in view);
}

export function isUITableViewFormat(view: any): boolean {
    return view && typeof view === 'object' && ['key', 'sections', 'sortby'].every(k => k in view);
}

/**
 * Parses a TblViewSpec into a TblView, filtering out columns not present in the column map.
 * @param colMap Map of column keys to TblCol objects
 * @param viewSpec TblViewSpec to parse
 * @returns TblView for rendering
 */
export function tblSpecParseToView(colMap: Map<string, TblCol>, viewSpec: TblViewSpec): TblView {
    const result: TblView = {
        cols: [],
        sections: [],
        sort: tableSortVM(),
    };

    let colCount = 0,
        newColumn: TblCol | undefined,
        sortCol = viewSpec.sortby,
        sortDir: TblSortDir = 'ASC',
        foundSortBy = false;

    // Use explicit sortdir if present, otherwise parse from sortby
    if (typeof viewSpec.sortdir === 'string') {
        const dirUC = viewSpec.sortdir.toUpperCase();
        sortDir = dirUC === 'DSC' || dirUC === 'DESC' ? 'DSC' : 'ASC';
        // Remove leading dash if present
        if (sortCol && sortCol.startsWith('-')) {
            sortCol = sortCol.substring(1);
        }
    } else if (sortCol && sortCol.startsWith('-')) {
        sortDir = 'DSC';
        sortCol = sortCol.substring(1);
    }

    viewSpec.sections.forEach(sec => {
        colCount = 0;
        sec.cols.forEach(col => {
            newColumn = colMap.get(col);
            if (!!newColumn) {
                colCount++;
                if (sortCol == col) {
                    foundSortBy = true;
                }
                result.cols.push(newColumn);
            }
        });
        if (colCount > 0) {
            result.sections.push({
                title: sec.title,
                span: colCount,
            });
        }
    });

    // use first sortable column if sortby not found
    if (!foundSortBy) {
        // eslint-disable-next-line no-console
        console.warn(
            '[tblSpecParseToView] sortCol not found in columns, defaulting to first sortable column. Original sortCol:',
            viewSpec.sortby,
            'Original sortDir:',
            sortDir,
        );
        for (let i = 0; i < result.cols.length; i++) {
            if (result.cols[i].hasSort) {
                sortCol = result.cols[i].key;
                sortDir = 'ASC';
                break;
            }
        }
    }

    result.sort = { col: sortCol, dir: sortDir };
    console.log('[tblSpecParseToView] viewSpec.sortby:', viewSpec.sortby, '→ sort:', JSON.stringify(result.sort));

    return result;
}

/**
 * Converts a LegacyTableView to a TblViewSpec for UI compatibility.
 * @param legacy The legacy table view object
 * @returns TblViewSpec
 */
export function legacyToTblViewSpec(legacy: LegacyTableView): TblViewSpec {
    // Always encode descending sort in sortby (with leading '-') and set sortdir to 'DSC' for descending
    let sortby = legacy.sortby ? legacy.sortby.replace(/^[-]+/, '') : 'id';
    let sortdir = 'ASC';
    if (legacy.sortby && legacy.sortby.startsWith('-')) {
        sortby = '-' + sortby;
        sortdir = 'DSC';
    }
    return {
        key: legacy.id,
        title: legacy.title,
        sections: [
            {
                title: 'Columns',
                cols: legacy.cols ? legacy.cols.split('|').filter(Boolean) : [],
            },
        ],
        sortby,
        sortdir,
        pgsize: typeof legacy.pgsize === 'number' ? legacy.pgsize : undefined,
    };
}

export function splitDefaultAndSessionViews(views: LegacyTableView[]): {
    defaultView: TblViewSpec | undefined;
    sessionViews: TblViewSpec[];
    allSpecs: TblViewSpec[];
} {
    const allSpecs = views.map(legacyToTblViewSpec);
    const defaultView = allSpecs.find(v => v && (v.key === 'default' || v.key === '1'));
    const sessionViews = allSpecs.filter(v => !(v.key === 'default' || v.key === '1'));
    return { defaultView, sessionViews, allSpecs };
}

export function createLegacyTableViewFromForm(form: TableViewFormData): LegacyTableView {
    // IMPORTANT: Always encode sort direction in sortby string for legacy format
    // Example: sortby: "created" (ascending), sortby: "-created" (descending)
    let sortby = form.sortby;
    if (form.sortdir === 'desc' && sortby && !sortby.startsWith('-')) {
        sortby = '-' + sortby;
    } else if (form.sortdir === 'asc' && sortby && sortby.startsWith('-')) {
        sortby = sortby.substring(1);
    }
    return {
        id: form.id || `view_${Date.now()}`,
        title: form.title,
        sortby,
        pgsize: form.pgsize,
        cols: '|' + form.selectedColumns.join('|') + '|',
    };
}
